import httpRequest from "@/auth/http-client";
import { buildPaginationQuery, buildSortQuery } from "@/helpers/request-helpers";
import IProduct, { IProductInput } from "@/interfaces/product.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

/** Alinhado ao backend: sortBy só code | title | brand | active; padrão title + asc. */
const DEFAULT_PRODUCT_FILTER_SORT_BY = "title";
const DEFAULT_PRODUCT_FILTER_SORT_DIRECTION = "asc";

class ProductService {
  private baseUrl = "api/v1/product";

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<IProduct>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const products = await httpRequest.get(`${this.baseUrl}?${paginationQuery}`);
    return products;
  }

  async filter(
    filter: IFilter<IProduct>[],
    pagination: RequestPagination,
    sortBy?: string | null,
    sortDirection?: string | null
  ): Promise<PaginatedResponse<IProduct>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const resolvedSortBy = sortBy?.trim() ? sortBy.trim() : DEFAULT_PRODUCT_FILTER_SORT_BY;
    const resolvedSortDirection = sortDirection?.trim()
      ? sortDirection.trim()
      : DEFAULT_PRODUCT_FILTER_SORT_DIRECTION;
    const sortQuery = buildSortQuery(resolvedSortBy, resolvedSortDirection);

    const response = await httpRequest.post(
      `${this.baseUrl}/filter?${paginationQuery}${sortQuery}`,
      {
        filter,
        sortBy: resolvedSortBy,
        sortDirection: resolvedSortDirection,
      }
    );

    return response;
  }

  async create(formData: IProductInput): Promise<IProduct> {
    // Remove o id do formData para criação, pois a API não aceita id na criação
    const { id, ...dataWithoutId } = formData;
    
    const result = await httpRequest.post(this.baseUrl, dataWithoutId);
    return result;
  }

  async getById(productId: number): Promise<IProduct> {
    const product = await httpRequest.get(`${this.baseUrl}/${productId}`);

    return product;
  }

  async update(formData: IProductInput, productId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${productId}`, formData);
  }

  async delete(productId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${productId}`);
  }

  async importProductsFromFile(file: File): Promise<boolean> {
    const formData = new FormData();
    formData.append("File", file);

    try {
      await httpRequest.post(`${this.baseUrl}/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return true;
    } catch (error) {
      return false;
    }
  }
}

const productService = new ProductService();
export default productService;
